import { useParams, Link, useNavigate } from 'react-router-dom'
import { useProducts } from '@/contexts/productContext.tsx'
import { Loading, Select } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowLeft } from '@tabler/icons-react'
import { useState, useEffect, useMemo } from 'react'
import { ProductProperty, ProductVariation, ProductPropertyValue, ProductPropertyDisplayKind, getRelevantGalleryImages } from '@/models/shop.ts'
import TemplateImageWithExamples from '@/components/TemplateImageWithExamples.tsx'
import { getImageTemplate } from '@/utils/imageTemplateRegistry.ts'
import Lightbox from 'yet-another-react-lightbox'
import { Inline } from 'yet-another-react-lightbox/plugins'
import 'yet-another-react-lightbox/styles.css'
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { useDrives } from '@/stores/useDrives.ts'
import { toast } from 'react-toastify'

export default function ProductPage() {
  const { product_sku, driveId } = useParams<{ product_sku: string; driveId: string }>()
  const { products, isLoading } = useProducts()
  const [selectedProperties, setSelectedProperties] = useState<Record<string, string>>({})
  const [selectedModProperties, setSelectedModProperties] = useState<Record<string, string>>({})
  const [currentVariation, setCurrentVariation] = useState<ProductVariation | null>(null)
  const navigate = useNavigate()
  const { currentDrive } = useDrives()

  // Find product by SKU
  const product = products.find(p => p.sku === product_sku)

  // Get properties that have variations
  const getAvailableProperties = (): ProductProperty[] => {
    if (!product || product.variations.length === 0) return []

    // Get all property types that exist in variations
    const propertyTypesInVariations = new Set<string>()
    product.variations.forEach(variation => {
      variation.property_value_links.forEach(link => {
        // Find the property that contains this property value
        const property = product.properties.find(prop =>
          prop.values.some(val => val.id === link.property_value.id)
        )
        if (property) {
          propertyTypesInVariations.add(property.property_type)
        }
      })
    })

    // Return only properties that have variations, sorted by sort_order
    return product.properties
      .filter(property => propertyTypesInVariations.has(property.property_type))
      .sort((a, b) => a.sort_order - b.sort_order)
  }

  // Get available property values for a specific property type based on current selection
  const getAvailablePropertyValues = (propertyType: string): ProductPropertyValue[] => {
    if (!product) return []

    const property = product.properties.find(p => p.property_type === propertyType)
    if (!property) return []

    // If always_display_all is true, show all values sorted by sort_order
    if (property.always_display_all) {
      return property.values.sort((a, b) => a.sort_order - b.sort_order)
    }

    // Get variations that match currently selected properties (excluding this property type)
    const otherSelectedProperties = Object.entries(selectedProperties)
      .filter(([type]) => type !== propertyType)

    const compatibleVariations = product.variations.filter(variation => {
      return otherSelectedProperties.every(([selectedType, selectedValueId]) => {
        return variation.property_value_links.some(link => {
          // Find the property that contains this property value
          const linkProperty = product.properties.find(prop =>
            prop.values.some(val => val.id === link.property_value.id)
          )
          return linkProperty?.property_type === selectedType &&
                 link.property_value.id === selectedValueId
        })
      })
    })

    // Get property values that exist in compatible variations
    const availableValueIds = new Set<string>()
    compatibleVariations.forEach(variation => {
      variation.property_value_links.forEach(link => {
        // Find the property that contains this property value
        const linkProperty = product.properties.find(prop =>
          prop.values.some(val => val.id === link.property_value.id)
        )
        if (linkProperty?.property_type === propertyType) {
          availableValueIds.add(link.property_value.id)
        }
      })
    })

    return property.values
      .filter(value => availableValueIds.has(value.id))
      .sort((a, b) => a.sort_order - b.sort_order)
  }

  // Find matching variation based on selected properties
  const findMatchingVariation = (): ProductVariation | null => {
    if (!product || Object.keys(selectedProperties).length === 0) return null

    return product.variations.find(variation => {
      const variationPropertyIds = variation.property_value_links.map(link => link.property_value.id)
      const selectedPropertyIds = Object.values(selectedProperties)

      // Check if all selected properties match this variation
      return selectedPropertyIds.every(selectedId => variationPropertyIds.includes(selectedId)) &&
             selectedPropertyIds.length === variationPropertyIds.length
    }) || null
  }

  // Handle property selection change
  const handlePropertyChange = (propertyType: string, valueId: string) => {
    const newSelection = { ...selectedProperties, [propertyType]: valueId }

    // Check all properties and auto-select first available value if current selection is invalid
    const availableProperties = getAvailableProperties()
    availableProperties.forEach(property => {
      const availableValues = getAvailablePropertyValuesForSelection(property.property_type, newSelection)

      // If this property has a selected value that's no longer available, or no value selected
      if (availableValues.length > 0) {
        if (!newSelection[property.property_type] ||
            !availableValues.some(v => v.id === newSelection[property.property_type])) {
          // Auto-select the first available value
          newSelection[property.property_type] = availableValues[0].id
        }
      }
    })

    setSelectedProperties(newSelection)
  }

  // Helper function to get available property values for a given selection
  const getAvailablePropertyValuesForSelection = (propertyType: string, selection: Record<string, string>): ProductPropertyValue[] => {
    if (!product) return []

    const property = product.properties.find(p => p.property_type === propertyType)
    if (!property) return []

    // If always_display_all is true, show all values sorted by sort_order
    if (property.always_display_all) {
      return property.values.sort((a, b) => a.sort_order - b.sort_order)
    }

    const otherSelectedProperties = Object.entries(selection)
      .filter(([type]) => type !== propertyType)

    const compatibleVariations = product.variations.filter(variation => {
      return otherSelectedProperties.every(([selectedType, selectedValueId]) => {
        return variation.property_value_links.some(link => {
          // Find the property that contains this property value
          const linkProperty = product.properties.find(prop =>
            prop.values.some(val => val.id === link.property_value.id)
          )
          return linkProperty?.property_type === selectedType &&
                 link.property_value.id === selectedValueId
        })
      })
    })

    const availableValueIds = new Set<string>()
    compatibleVariations.forEach(variation => {
      variation.property_value_links.forEach(link => {
        // Find the property that contains this property value
        const linkProperty = product.properties.find(prop =>
          prop.values.some(val => val.id === link.property_value.id)
        )
        if (linkProperty?.property_type === propertyType) {
          availableValueIds.add(link.property_value.id)
        }
      })
    })

    return property.values
      .filter(value => availableValueIds.has(value.id))
      .sort((a, b) => a.sort_order - b.sort_order)
  }

  // Initialize with first variation on product load
  useEffect(() => {
    if (product && product.variations.length > 0 && Object.keys(selectedProperties).length === 0) {
      // Sort variations by sort_order and pick the first one
      const firstVariation = [...product.variations].sort((a, b) => a.sort_order - b.sort_order)[0]

      if (firstVariation) {
        const initialSelection: Record<string, string> = {}

        // Build initial selection from first variation's property values
        firstVariation.property_value_links.forEach(link => {
          const property = product.properties.find(prop =>
            prop.values.some(val => val.id === link.property_value.id)
          )
          if (property) {
            initialSelection[property.property_type] = link.property_value.id
          }
        })

        setSelectedProperties(initialSelection)
      }
    }
    
    // Initialize mod properties with first values
    if (product && Object.keys(selectedModProperties).length === 0) {
      const modProps = product.properties.filter(p => p.is_mod)
      if (modProps.length > 0) {
        const initialModSelection: Record<string, string> = {}
        modProps.forEach(prop => {
          if (prop.values.length > 0) {
            // Select first value by default
            initialModSelection[prop.tech_id] = prop.values[0].tech_id
          }
        })
        setSelectedModProperties(initialModSelection)
      }
    }
  }, [product])

  // Update current variation when selection changes
  useEffect(() => {
    setCurrentVariation(findMatchingVariation())
  }, [selectedProperties, product])

  // Get current price
  const getCurrentPrice = (): string => {
    if (currentVariation && currentVariation.price) {
      return currentVariation.price
    }
    return product?.base_price || '0'
  }

  // Get selected property value IDs for gallery filtering
  const selectedPropertyValueIds = useMemo(() => {
    return Object.values(selectedProperties)
  }, [selectedProperties])

  // Get relevant gallery images
  const relevantGalleryImages = useMemo(() => {
    if (!product) return []
    return getRelevantGalleryImages(product, selectedPropertyValueIds)
  }, [product, selectedPropertyValueIds])

  // Check if preview should be replaced
  const shouldReplacePreview = relevantGalleryImages.some(img => img.replace_preview)
  const primaryImage = shouldReplacePreview && relevantGalleryImages.length > 0
    ? relevantGalleryImages[0]
    : null

  // Prepare slides for lightbox
  const lightboxSlides = useMemo<any>(() => {
    const slides: Array<{
      type: 'custom-slide'
      src: string
      title: string
      description: string
      template?: any
    }> = []

    // Add primary image (either replaced or original preview)
    if (primaryImage) {
      const imageTemplate = getImageTemplate(primaryImage.image_template_id)
      slides.push({
        type: 'custom-slide',
        src: primaryImage.image,
        title: primaryImage.title,
        description: primaryImage.description,
        template: imageTemplate
      })
    } else if (product) {
      const imageTemplate = getImageTemplate(product.image_template_id)
      slides.push({
        type: 'custom-slide',
        src: product.preview_image || '/placeholder-product.jpg',
        title: product.title,
        description: 'Produktvorschau',
        template: imageTemplate,
      })
    }

    // Add remaining gallery images
    const startIndex = shouldReplacePreview ? 1 : 0
    relevantGalleryImages.slice(startIndex).forEach(img => {
      const imageTemplate = getImageTemplate(img.image_template_id)
      slides.push({
        type: 'custom-slide',
        src: img.image,
        title: img.title,
        description: img.description,
        template: imageTemplate,
      })
    })

    return slides
  }, [product, primaryImage, relevantGalleryImages, shouldReplacePreview])

  function onSelectImageClick() {
    if (currentDrive && currentDrive.is_demo) {
      toast('Die Bilderauswahl wird erst mit dem Kauf freigeschaltet.', {
        autoClose: 5000,
        position: 'top-center',

      })
      return
    }

    // Build URL with mod properties as query parameters
    const modParams = new URLSearchParams()
    Object.entries(selectedModProperties).forEach(([key, value]) => {
      if (value) {
        modParams.append(key, value)
      }
    })
    
    const modParamsString = modParams.toString()
    const url = `${currentVariation!.sku_suffix}/select${modParamsString ? `?${modParamsString}` : ''}`
    navigate(url)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">Produkt nicht gefunden</h1>
          <p className="text-center opacity-75">Das angeforderte Produkt konnte nicht gefunden werden.</p>
          <Link to={`/d/${driveId}/shop`}>
            <Button color="primary" variant="outline">
              <IconArrowLeft size={20} />
              Zurück zum Shop
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const availableProperties = getAvailableProperties()
  
  // Get mod properties (is_mod: true)
  const modProperties = product?.properties.filter(p => p.is_mod) || []

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back to shop button */}
      <div className="mb-6">
        <Link to={`/d/${driveId}/shop`}>
          <Button color="primary" variant="outline" size="sm">
            <IconArrowLeft size={20} />
            Zurück zum Shop
          </Button>
        </Link>
      </div>

      {/* Product content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left side - Product images with lightbox */}
        <div className="flex justify-center items-start">
          <div className="product-lightbox w-full flex-1 h-full relative">
            {lightboxSlides.length > 0 && (
              <Lightbox
                plugins={[Inline, Thumbnails]}
                slides={lightboxSlides}
                carousel={{
                  finite: true
                }}
                thumbnails={{ vignette: false, position: 'bottom' }}
                render={{
                  slide: ({ slide }: { slide: any }) =>
                    slide.type === 'custom-slide' ? (
                      <div className="w-full">
                        {slide.template ? (
                          <TemplateImageWithExamples
                            backgroundImage={slide.src}
                            template={slide.template}
                            className="max-w-full max-h-full object-contain select-none pointer-events-none"
                          />
                        ) : (
                          <img
                            src={slide.src}
                            alt={slide.title}
                            className="max-w-full max-h-full object-contain select-none pointer-events-none"
                          />
                        )}
                      </div>
                    ) : undefined,
                  thumbnail: ({ slide }: { slide: any }) => slide.type === 'custom-slide' ? (
                    <div className="w-full">
                      {/* ts-ignore */}
                      {slide.template ? (
                        <TemplateImageWithExamples
                          backgroundImage={slide.src}
                          template={slide.template}
                          className="max-w-full max-h-full object-contain select-none pointer-events-none"
                        />
                      ) : (
                        <img
                          src={slide.src}
                          alt={slide.title}
                          className="max-w-full max-h-full object-contain select-none pointer-events-none"
                        />
                      )}
                    </div>
                  ) : undefined,
                }}
              />
            )}
            <div className='pointer-events-none absolute top-0 right-0 bg-[white] rounded p-1'>
              <img className='h-[6rem]' src="/shopAssets/fotopapier-forstwirtschaft.webp" alt="" />
            </div>
          </div>
        </div>

        {/* Right side - Product details */}
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{product.title}</h1>
          </div>

          {/* Description */}
          {product.description && (
            <div>
              <h2 className="text-xl font-semibold mb-3">Beschreibung</h2>
              <p className="text-base leading-relaxed whitespace-pre-wrap">
                {product.description}
              </p>
            </div>
          )}

          {/* Property Selection - Both regular and mod properties */}
          {(availableProperties.length > 0 || modProperties.length > 0) && (
            <div>
              <h2 className="text-xl font-semibold mb-3">Optionen</h2>
              <div className="flex flex-col gap-4">
                {/* Regular properties */}
                {availableProperties.map(property => {
                  const availableValues = getAvailablePropertyValues(property.property_type)
                  if (availableValues.length === 0) return null

                  // Check if the original property has 3 or fewer values total
                  const originalProperty = product.properties.find(p => p.property_type === property.property_type)
                  const useClickableElements = originalProperty && (originalProperty.display_kind === ProductPropertyDisplayKind.MULTI_BOXES ? true : originalProperty.display_kind !== ProductPropertyDisplayKind.SELECT_DROPDOWN && originalProperty.values.length <= 3)

                  return (
                    <div key={property.id}>
                      <label className="block text-sm font-medium mb-2">
                        {property.name}
                      </label>

                      {useClickableElements ? (
                        // Clickable elements for 3 or fewer options
                        <div className="flex flex-wrap gap-2">
                          {availableValues.map(value => {
                            const isSelected = selectedProperties[property.property_type] === value.id
                            return (
                              <button
                                key={value.id}
                                onClick={() => handlePropertyChange(property.property_type, value.id)}
                                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                  isSelected 
                                    ? 'border-primary bg-primary text-primary-content' 
                                    : 'border-base-300 bg-base-100 hover:border-primary hover:bg-base-200'
                                }`}
                              >
                                {value.value}
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        // Select dropdown for more than 3 options
                        <Select
                          value={selectedProperties[property.property_type] || ''}
                          onChange={(e) => handlePropertyChange(property.property_type, e.target.value)}
                          className="w-full"
                        >
                          <option value="">Bitte wählen...</option>
                          {availableValues.map(value => (
                            <option key={value.id} value={value.id}>
                              {value.value}
                            </option>
                          ))}
                        </Select>
                      )}
                    </div>
                  )
                })}
                
                {/* Mod properties (is_mod: true) */}
                {modProperties.map(property => {
                  // Check if the property has 3 or fewer values total
                  const useClickableElements = property.display_kind === ProductPropertyDisplayKind.MULTI_BOXES ? true : property.display_kind !== ProductPropertyDisplayKind.SELECT_DROPDOWN && property.values.length <= 3

                  return (
                    <div key={property.id}>
                      <label className="block text-sm font-medium mb-2">
                        {property.name}
                      </label>

                      {useClickableElements ? (
                        // Clickable elements for 3 or fewer options
                        <div className="flex flex-wrap gap-2">
                          {property.values.map(value => {
                            const isSelected = selectedModProperties[property.tech_id] === value.tech_id
                            return (
                              <button
                                key={value.id}
                                onClick={() => setSelectedModProperties(prev => ({
                                  ...prev,
                                  [property.tech_id]: value.tech_id
                                }))}
                                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                                  isSelected 
                                    ? 'border-primary bg-primary text-primary-content' 
                                    : 'border-base-300 bg-base-100 hover:border-primary hover:bg-base-200'
                                }`}
                              >
                                {value.value}
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        // Select dropdown for more than 3 options
                        <Select
                          value={selectedModProperties[property.tech_id] || ''}
                          onChange={(e) => setSelectedModProperties(prev => ({
                            ...prev,
                            [property.tech_id]: e.target.value
                          }))}
                          className="w-full"
                        >
                          <option value="">Bitte wählen...</option>
                          {property.values.map(value => (
                            <option key={value.id} value={value.tech_id}>
                              {value.value}
                            </option>
                          ))}
                        </Select>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Price */}
          <div>
            <h2 className="text-xl font-semibold mb-2">Preis</h2>
            <div className="text-2xl font-bold text-primary">
              {formatPrice(getCurrentPrice())}
            </div>
            {currentVariation && (
              <p className="text-sm opacity-75 mt-1">
                {currentVariation.property_value_display}
              </p>
            )}
          </div>

          {/* Product type badge */}
          <div>
            {product.is_print_product && (
              <span className="badge badge-primary">
                Druckprodukt
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-3 mt-auto">
            <Button
              color="primary"
              size="lg"
              className="w-full"
              disabled={availableProperties.length > 0 && !currentVariation}
              onClick={onSelectImageClick}
            >
              Bilder auswählen
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to format price
function formatPrice(price: string): string {
  return `€${parseFloat(price).toFixed(2)}`
}
